import React, { FC } from 'react'
// PRO-130: a deep antd/es/* subpath, NOT the `antd` root barrel — this seam is
// on the boot-static graph via BreadCrumb (see src/antd/Empty.tsx).
import AntdDropdown from 'antd/es/dropdown'
import type { DropdownProps } from 'antd/es/dropdown'
import DropdownButton from 'antd/es/dropdown/dropdown-button'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import { Dropdown as AntdSeamDropdown } from './Dropdown.antd'
import { DropdownShadcn } from './Dropdown.shadcn'

/**
 * Compat seam for antd's `Dropdown` — ported behind the design toggle together
 * with `../Select`, because both are POPUP surfaces and the two questions they
 * raise (where does the popup mount, at what z-index) are worth answering
 * once. `../Select/index.tsx` carries that reasoning; read it first.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders
 * `./Dropdown.antd` with the props it was given: same element, same class
 * names, same DOM. The only difference from the previous
 * `src/antd/Dropdown.tsx` — which IS `./Dropdown.antd`, moved — is one extra
 * component in the React tree, which renders nothing of its own.
 * `../__tests__/dropdown-seam-parity.test.tsx` asserts that node for node, and
 * it is the test to keep green.
 *
 * Like `../Select`, this seam has always SHADOWED antd rather than re-exported
 * it (`getPopupContainer` defaults to the trigger's parent), so "unchanged"
 * means unchanged versus `./Dropdown.antd`, never versus `antd/es/dropdown`.
 * The 3 files still importing `Dropdown` from raw `antd` are the documented
 * deferred residual; the toggle does not reach them.
 *
 * ---------------------------------------------------------------------------
 * `Dropdown.Button` IS NOT DISPATCHED
 *
 * A static is read at the call site, where no hook can run. It stays antd's
 * own in both modes — which is correct rather than merely convenient: no call
 * site in this repo renders `Dropdown.Button`, so nothing is half-switched by
 * it, and dropping the static would change the shape of the export for the
 * sake of dead code.
 */
const DropdownDispatcher: FC<DropdownProps> = (props) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <DropdownShadcn {...props} />

  return <AntdSeamDropdown {...props} />
}

DropdownDispatcher.displayName = 'Dropdown'

const Dropdown = DropdownDispatcher as FC<DropdownProps> & {
  Button: typeof DropdownButton
}

Dropdown.Button = AntdDropdown.Button

export { Dropdown }
